import {
  ETIQUETA_NIVEL_DE_ACCESO,
  NIVELES_DE_ACCESO,
  SECCIONES_DE_ACCESO,
  SECCIONES_DE_ACCESO_EN,
  type Accesos,
  type NivelDeAcceso,
  type Usuario,
} from '@cnc/contracts';
import {useState, type ReactNode} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useEditarAccesos} from '../api/sesion';
import {idiomaActual, tr} from '../i18n/idioma';
import {AYUDA_USUARIOS as A} from './ayuda-general';
import {NotaDeAyuda} from './nota-de-ayuda';

const ETIQUETA_EN: Record<NivelDeAcceso, string> = {no: 'No access', ver: 'View', editar: 'View and edit'};
const nombreDeNivel = (n: NivelDeAcceso) => (idiomaActual() === 'es' ? ETIQUETA_NIVEL_DE_ACCESO[n] : ETIQUETA_EN[n]);
const nombreDe = (clave: string, es: string) => (idiomaActual() === 'es' ? es : (SECCIONES_DE_ACCESO_EN[clave] ?? es));

// Secciones y pestanas de una persona de Edicion o Consulta (decision 0062):
// por seccion "Lo de su rol", "No ve", "Ve" o "Ve y edita"; cada pestana
// "Igual que la sección" o su propio nivel. Consulta no tiene "Ve y edita".
export function AccesosDeUsuario({usuario, onCerrar}: {usuario: Usuario; onCerrar: () => void}) {
  const guardar = useEditarAccesos();
  const [accesos, setAccesos] = useState<Accesos>(usuario.accesos ?? {});
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  const niveles = NIVELES_DE_ACCESO.filter(n => usuario.rol !== 'viewer' || n !== 'editar');
  const poner = (clave: string, valor: string) =>
    setAccesos(a => {
      const n = {...a};
      if (valor) n[clave] = valor as NivelDeAcceso;
      else delete n[clave];
      return n;
    });

  const enviar = async (valor: Accesos | null) => {
    setMensaje(null);
    try {
      const r = await guardar.mutateAsync({id: usuario.id, accesos: valor});
      setAccesos(r.accesos ?? {});
      setMensaje({
        texto: tr(
          'Guardado. La persona lo verá la próxima vez que abra o recargue la página.',
          'Saved. The person will see it the next time they open or reload the page.',
        ),
        error: false,
      });
    } catch (e) {
      setMensaje({
        texto: e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'),
        error: true,
      });
    }
  };

  const selector = (clave: string, nombre: string, vacio: string) => (
    <select
      value={accesos[clave] ?? ''}
      onChange={e => poner(clave, e.target.value)}
      aria-label={tr(`Acceso a ${nombre}`, `Access to ${nombre}`)}
      className="rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
    >
      <option value="">{vacio}</option>
      {niveles.map(n => (
        <option key={n} value={n}>
          {nombreDeNivel(n)}
        </option>
      ))}
    </select>
  );

  return (
    <div
      className="space-y-3 rounded-lg border border-verde/40 bg-white p-4"
      role="region"
      aria-label={tr(`Secciones de ${usuario.nombre}`, `Sections of ${usuario.nombre}`)}
      data-prueba="accesos-de-usuario"
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="font-medium">
          {tr(`Secciones y pestañas de ${usuario.nombre}`, `Sections and tabs of ${usuario.nombre}`)}
          <NotaDeAyuda texto={A.accesos} />
        </h2>
        <button type="button" onClick={onCerrar} aria-label={tr('Cerrar', 'Close')} className="text-apagado">
          ×
        </button>
      </div>
      <p className="text-xs text-apagado">
        {usuario.rol === 'viewer'
          ? tr(
              'Tiene rol de Consulta: en las secciones que vea, solo consulta.',
              'They have the Viewer role: in the sections they see, they can only view.',
            )
          : tr(
              'Tiene rol de Edición: en cada sección puede no verla, solo verla o verla y editarla.',
              'They have the Editor role: in each section they can not see it, only view it, or view and edit it.',
            )}{' '}
        {tr(
          'Administración siempre es solo para administradores; su cuenta, la guía y la ayuda las tiene siempre.',
          'Administration is always only for administrators; their account, the guide and the help are always available.',
        )}
      </p>
      <table className="w-full max-w-2xl text-sm">
        <tbody>
          {SECCIONES_DE_ACCESO.map(s => (
            <SeccionDeAccesos key={s.clave} seccion={s} selector={selector} nivelDeSeccion={accesos[s.clave]} />
          ))}
        </tbody>
      </table>
      {mensaje && (
        <p
          role={mensaje.error ? 'alert' : 'status'}
          className={`text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}
        >
          {mensaje.texto}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void enviar(accesos)}
          disabled={guardar.isPending}
          className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {tr('Guardar secciones', 'Save sections')}
        </button>
        <button
          type="button"
          onClick={() => void enviar(null)}
          disabled={guardar.isPending}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
        >
          {tr('Quitar los límites (todo lo de su rol)', 'Remove the limits (everything of their role)')}
        </button>
      </div>
    </div>
  );
}

function SeccionDeAccesos({
  seccion: s,
  selector,
  nivelDeSeccion,
}: {
  seccion: (typeof SECCIONES_DE_ACCESO)[number];
  selector: (clave: string, nombre: string, vacio: string) => ReactNode;
  nivelDeSeccion: NivelDeAcceso | undefined;
}) {
  const nombre = nombreDe(s.clave, s.nombre);
  const heredado = nivelDeSeccion
    ? tr(
        `Igual que la sección (${nombreDeNivel(nivelDeSeccion)})`,
        `Same as section (${nombreDeNivel(nivelDeSeccion)})`,
      )
    : tr('Igual que la sección', 'Same as section');
  return (
    <>
      <tr className="border-t border-linea/70">
        <td className="py-1.5 pr-3 font-medium">{nombre}</td>
        <td className="py-1.5">{selector(s.clave, nombre, tr('Lo de su rol', 'As their role'))}</td>
      </tr>
      {s.pestanas.map(p => {
        const clave = `${s.clave}.${p.clave}`;
        const nombreP = nombreDe(clave, p.nombre);
        return (
          <tr key={clave}>
            <td className="py-1 pr-3 pl-5 text-apagado">↳ {nombreP}</td>
            <td className="py-1">{selector(clave, `${nombre} → ${nombreP}`, heredado)}</td>
          </tr>
        );
      })}
    </>
  );
}
